import { ref, computed, onUnmounted } from 'vue'

// Distance in meters between two lat/lng pairs using the equirectangular
// approximation. Plenty accurate at the scale of a delivery; cheaper than
// the full Haversine for a 30-Hz hot loop.
function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const x = (toRad(lng2) - toRad(lng1)) * Math.cos((toRad(lat1) + toRad(lat2)) / 2)
  const y = toRad(lat2) - toRad(lat1)
  return Math.sqrt(x * x + y * y) * R
}

function csrfToken() {
  const m = document.querySelector('meta[name="csrf-token"]')
  return m ? m.getAttribute('content') : ''
}

/**
 * Driver-side geolocation watcher. Opt-in via start(); auto-stops via stop()
 * or when the delivery transitions to a terminal state.
 *
 * Filters before POST:
 *  - At least one fix per `minIntervalMs` (heartbeat, even when stationary)
 *  - Skip duplicate fixes (< minDistanceM)
 *
 * Server-side rate limit (Rack::Attack) is 60/min/IP — this composable
 * stays well under that with defaults (4/min stationary, ~1/min moving slow,
 * up to ~12/min on a fast bike — well below 60).
 */
export function useDeliveryGeolocation({
  deliveryId,
  minIntervalMs = 15000,
  minDistanceM  = 25,
} = {}) {
  const isSharing  = ref(false)
  const lastPosition = ref(null)
  const lastSentAt = ref(null)
  const error      = ref(null)

  let watchId = null
  let lastSentCoord = null

  const isSupported = computed(() =>
    typeof navigator !== 'undefined' && 'geolocation' in navigator
  )

  async function postPosition(coords, timestamp) {
    try {
      const body = {
        position: {
          lat:         coords.latitude,
          lng:         coords.longitude,
          accuracy_m:  coords.accuracy ? Math.round(coords.accuracy) : null,
          heading:     Number.isFinite(coords.heading) ? Math.round(coords.heading) : null,
          speed_kmh:   Number.isFinite(coords.speed) ? +(coords.speed * 3.6).toFixed(2) : null,
          recorded_at: new Date(timestamp || Date.now()).toISOString(),
        },
      }
      const res = await fetch(`/deliveries/${deliveryId}/positions`, {
        method:  'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'Accept':       'application/json',
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (data?.error === 'delivery_terminal') {
          stop()  // server tells us the delivery is done — stop the watch
        } else {
          error.value = data?.error || `HTTP ${res.status}`
        }
        return false
      }
      lastSentAt.value = new Date()
      return true
    } catch (e) {
      error.value = e?.message || 'network'
      return false
    }
  }

  function onFix(pos) {
    lastPosition.value = {
      lat:       pos.coords.latitude,
      lng:       pos.coords.longitude,
      accuracy:  pos.coords.accuracy,
      heading:   pos.coords.heading,
      speed:     pos.coords.speed,
      timestamp: pos.timestamp,
    }
    error.value = null

    const now = Date.now()
    const sentAgo = lastSentAt.value ? (now - lastSentAt.value.getTime()) : Infinity
    const movedFar = lastSentCoord
      ? distanceMeters(lastSentCoord.lat, lastSentCoord.lng, pos.coords.latitude, pos.coords.longitude) >= minDistanceM
      : true

    if (!movedFar && sentAgo < minIntervalMs) return

    lastSentCoord = { lat: pos.coords.latitude, lng: pos.coords.longitude }
    postPosition(pos.coords, pos.timestamp)
  }

  function onError(err) {
    error.value = err.message || `geolocation error ${err.code}`
    // Permission denial or unavailable hardware — bail out so the UI can
    // re-prompt cleanly on the next start().
    if (err.code === 1 /* PERMISSION_DENIED */ || err.code === 2 /* POSITION_UNAVAILABLE */) {
      stop()
    }
  }

  function start() {
    if (!isSupported.value) {
      error.value = 'geolocation_unsupported'
      return false
    }
    if (isSharing.value) return true

    watchId = navigator.geolocation.watchPosition(onFix, onError, {
      enableHighAccuracy: true,
      maximumAge:         5000,
      timeout:            20000,
    })
    isSharing.value = true
    return true
  }

  function stop() {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId)
      watchId = null
    }
    isSharing.value = false
  }

  onUnmounted(() => stop())

  return { isSharing, lastPosition, lastSentAt, error, isSupported, start, stop }
}
